import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/feature/PageHero';
import { useSEO } from '../../utils/seo';

const ServicesPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://okayama-pet-funeral.example.com';

  useSEO({
    title: 'ペット葬について｜岡山市のペット火葬・納骨サービス｜おかやまペット葬儀株式会社',
    description: '岡山市のペット葬儀の流れとサービスプラン。個別火葬、合同火葬、納骨、永代供養まで。小鳥、ハムスター、猫、小型犬、大型犬対応。24時間365日対応。基本プラン15,000円から。',
    keywords: '岡山市,ペット葬儀,火葬の流れ,個別火葬,合同火葬,納骨,永代供養,サービスプラン,料金プラン',
    canonical: `${siteUrl}/services`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${siteUrl}/services#service`,
      name: 'ペット葬儀・火葬サービス',
      description: 'ペット葬儀の流れとサービスプラン。個別火葬、合同火葬、納骨、永代供養まで対応。',
      provider: {
        '@type': 'LocalBusiness',
        name: 'おかやまペット葬儀株式会社',
        '@id': `${siteUrl}/#organization`,
      },
      areaServed: {
        '@type': 'City',
        name: '岡山市',
      },
      offers: [
        {
          '@type': 'Offer',
          name: '基本プラン',
          price: '15000',
          priceCurrency: 'JPY',
          description: '合同火葬、合同納骨・永代供養、火葬証明書発行、24時間対応',
        },
        {
          '@type': 'Offer',
          name: 'おすすめプラン',
          price: '30000',
          priceCurrency: 'JPY',
          description: '個別火葬、ご家族立ち会い可能、ご遺骨返却、お骨上げ、火葬証明書発行、24時間対応',
        },
        {
          '@type': 'Offer',
          name: 'プレミアムプラン',
          price: '50000',
          priceCurrency: 'JPY',
          description: '個別火葬、ご家族立ち会い可能、個別納骨棚（3年間）、お骨上げ、仏具セット付き、火葬証明書発行、24時間対応、定期供養祭ご招待',
        },
      ],
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <PageHero title="ペット葬について" subtitle="Pet Funeral Services" />

      {/* ペット葬の説明セクション */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-gray-800 mb-6">
              人と同じように葬儀をしてあげませんか？
            </h2>
            <div className="w-24 h-1 bg-purple-500 mx-auto mb-8"></div>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6">
              大切な家族の一員として、共に過ごしてきたペット。その最期の時を、人と同じように心を込めて見送ってあげたいと思いませんか。
            </p>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6">
              おかやまペット葬儀株式会社では、ペットも大切な家族の一員として、人と同じように丁寧な葬儀を執り行っております。個別火葬、合同火葬、納骨、永代供養まで、ご家族のご希望に合わせたプランをご用意しております。
            </p>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              24時間365日対応しておりますので、いつでもお気軽にご相談ください。経験豊富なスタッフが、ご家族の気持ちに寄り添い、心を込めてお手伝いさせていただきます。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="bg-purple-50 rounded-2xl p-6 text-center">
              <div className="w-16 h-16 bg-purple-500 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                心を込めた葬儀
              </h3>
              <p className="text-sm text-gray-600">
                人と同じように、丁寧で心のこもった葬儀を執り行います
              </p>
            </div>

            <div className="bg-pink-50 rounded-2xl p-6 text-center">
              <div className="w-16 h-16 bg-pink-500 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                24時間365日対応
              </h3>
              <p className="text-sm text-gray-600">
                いつでもお電話ください。すぐに対応いたします
              </p>
            </div>

            <div className="bg-purple-50 rounded-2xl p-6 text-center">
              <div className="w-16 h-16 bg-purple-500 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-hand-heart-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                寄り添うサポート
              </h3>
              <p className="text-sm text-gray-600">
                ご家族の気持ちに寄り添い、丁寧にサポートいたします
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ペット葬の流れセクション */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-gray-800 mb-4">
              ペット葬の流れ
            </h1>
          </div>

          <div className="space-y-8">
            {[
              {
                step: 1,
                title: 'お電話・お問い合わせ',
                description:
                  'まずはお電話またはお問い合わせフォームからご連絡ください。24時間365日対応しております。ご希望の日時やプランについてご相談させていただきます。',
                time: '即日対応可能',
              },
              {
                step: 2,
                title: 'ご遺体のお預かり',
                description:
                  'ご自宅までお伺いし、大切なペットをお預かりいたします。または、直接当院までお連れいただくことも可能です。丁寧に扱わせていただきます。',
                time: '30分〜1時間',
              },
              {
                step: 3,
                title: 'お別れの時間',
                description:
                  '火葬前に、ご家族でゆっくりとお別れの時間をお過ごしいただけます。お花やお好きだったおやつなどをお供えいただけます。',
                time: '30分程度',
              },
              {
                step: 4,
                title: '火葬',
                description:
                  '個別火葬の場合は、ご家族立ち会いのもと、心を込めて火葬いたします。最新の設備で、丁寧に火葬を行います。',
                time: '1〜2時間',
              },
              {
                step: 5,
                title: 'お骨上げ・納骨',
                description:
                  '火葬後、お骨上げを行います。ご遺骨をお返しするか、当院での納骨をお選びいただけます。個別納骨または合同納骨をご用意しております。',
                time: '30分程度',
              },
              {
                step: 6,
                title: 'アフターケア',
                description:
                  '納骨後も、定期的な供養祭の開催や、いつでもお参りいただける環境を整えております。ペットロスのご相談にも対応いたします。',
                time: '継続的なサポート',
              },
            ].map((item, index) => (
              <div key={item.step} className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-2xl shadow-lg">
                    {item.step}
                  </div>
                  {index < 5 && (
                    <div className="w-0.5 h-16 bg-purple-200 mx-auto mt-4"></div>
                  )}
                </div>
                <div className="flex-1 bg-white rounded-2xl p-6 shadow-md">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-3">
                    {item.description}
                  </p>
                  <div className="inline-flex items-center text-xs text-purple-600 bg-purple-50 px-3 py-1 rounded-full">
                    <i className="ri-time-line mr-1"></i>
                    {item.time}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 火葬についての説明セクション */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-purple-100 to-pink-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-gray-800 mb-4">
              火葬について
            </h2>
            <p className="text-base text-gray-700 mt-4">
              ご家族のご希望に合わせて、合同火葬と個別火葬の2つの方法をご用意しております
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {/* 合同火葬 */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg flex flex-col">
              <div className="bg-gradient-to-r from-purple-200 to-purple-300 px-6 py-8 text-center">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-group-line text-4xl text-purple-500"></i>
                </div>
                <h3 className="text-2xl font-serif font-bold text-gray-800">
                  合同火葬
                </h3>
              </div>

              <div className="p-8 flex-1 flex flex-col">
                <p className="text-sm text-gray-700 leading-relaxed mb-6">
                  他のペットちゃんと一緒に火葬を行う方法です。火葬後は合同納骨となり、永代供養させていただきます。費用を抑えたい方や、他のペットちゃんと一緒に眠らせてあげたい方におすすめです。
                </p>

                <div className="space-y-4 flex-1">
                  <div className="flex items-start">
                    <i className="ri-check-line text-purple-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <div>
                      <h4 className="font-bold text-gray-800 text-sm mb-1">
                        費用を抑えられる
                      </h4>
                      <p className="text-xs text-gray-600">
                        個別火葬に比べて費用を抑えることができます
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <i className="ri-check-line text-purple-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <div>
                      <h4 className="font-bold text-gray-800 text-sm mb-1">
                        永代供養
                      </h4>
                      <p className="text-xs text-gray-600">
                        合同納骨後、永代供養させていただきます
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <i className="ri-check-line text-purple-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <div>
                      <h4 className="font-bold text-gray-800 text-sm mb-1">
                        他のペットと一緒
                      </h4>
                      <p className="text-xs text-gray-600">
                        他のペットちゃんと一緒に眠ることができます
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <i className="ri-close-line text-gray-400 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <div>
                      <h4 className="font-bold text-gray-800 text-sm mb-1">
                        ご遺骨の返却なし
                      </h4>
                      <p className="text-xs text-gray-600">
                        ご遺骨をお返しすることはできません
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <i className="ri-close-line text-gray-400 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <div>
                      <h4 className="font-bold text-gray-800 text-sm mb-1">
                        立ち会いなし
                      </h4>
                      <p className="text-xs text-gray-600">
                        火葬への立ち会いはできません
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <a
                    href="/pricing"
                    className="block w-full py-3 rounded-full font-medium transition-colors cursor-pointer bg-white text-purple-600 border-2 border-purple-500 hover:bg-purple-50 text-center whitespace-nowrap"
                  >
                    料金詳細を見る
                  </a>
                </div>
              </div>
            </div>

            {/* 個別火葬 */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg md:scale-105 ring-4 ring-pink-300 flex flex-col">
              <div className="bg-gradient-to-r from-pink-200 to-pink-300 px-6 py-8 text-center">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-heart-line text-4xl text-pink-500"></i>
                </div>
                <h3 className="text-2xl font-serif font-bold text-gray-800">
                  個別火葬
                </h3>
              </div>

              <div className="p-8 flex-1 flex flex-col">
                <p className="text-sm text-gray-700 leading-relaxed mb-6">
                  ペットちゃん一体ずつ、個別に火葬を行う方法です。ご家族立ち会いのもと、心を込めて火葬いたします。ご遺骨をお返しすることができ、ご自宅で供養したり、当院の個別納骨棚に納骨することも可能です。
                </p>

                <div className="space-y-4 flex-1">
                  <div className="flex items-start">
                    <i className="ri-check-line text-pink-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <div>
                      <h4 className="font-bold text-gray-800 text-sm mb-1">
                        ご家族立ち会い可能
                      </h4>
                      <p className="text-xs text-gray-600">
                        火葬に立ち会い、最期のお別れができます
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <i className="ri-check-line text-pink-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <div>
                      <h4 className="font-bold text-gray-800 text-sm mb-1">
                        ご遺骨の返却
                      </h4>
                      <p className="text-xs text-gray-600">
                        ご遺骨をお返しし、ご自宅で供養できます
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <i className="ri-check-line text-pink-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <div>
                      <h4 className="font-bold text-gray-800 text-sm mb-1">
                        お骨上げ
                      </h4>
                      <p className="text-xs text-gray-600">
                        人と同じように、お骨上げを行います
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <i className="ri-check-line text-pink-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <div>
                      <h4 className="font-bold text-gray-800 text-sm mb-1">
                        個別納骨も可能
                      </h4>
                      <p className="text-xs text-gray-600">
                        当院の個別納骨棚に納骨することもできます
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <i className="ri-check-line text-pink-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                    <div>
                      <h4 className="font-bold text-gray-800 text-sm mb-1">
                        丁寧な対応
                      </h4>
                      <p className="text-xs text-gray-600">
                        一体ずつ丁寧に、心を込めて火葬いたします
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <a
                    href="/pricing"
                    className="block w-full py-3 rounded-full font-medium transition-colors cursor-pointer bg-purple-500 text-white hover:bg-purple-600 text-center whitespace-nowrap"
                  >
                    料金詳細を見る
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-sm text-gray-700 mb-6">
              どちらのプランが良いか迷われている方は、お気軽にご相談ください。<br />
              ご家族のご希望やご予算に合わせて、最適なプランをご提案させていただきます。
            </p>
            <a
              href="/contact"
              className="inline-block bg-purple-500 text-white px-8 py-3 rounded-full font-medium hover:bg-purple-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              お問い合わせはこちら
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
